import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ProductCard } from '@/components/product';
import { useProducts, useCategories } from '@/hooks';
import { cn } from '@/lib/utils';
import { Category } from '@/types';

export const CatalogPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // URL'den filtreleri oku (page hariç - infinite scroll yönetecek)
  const filters = useMemo(() => ({
    categoryId: searchParams.get('categoryId') || undefined,
    categorySlug: searchParams.get('categorySlug') || undefined,
    productType: (searchParams.get('productType') as 'chair' | 'table' | 'set') || undefined,
    search: searchParams.get('search') || undefined,
    featured: searchParams.get('featured') === 'true' || undefined,
  }), [searchParams]);

  const { useInfiniteProductList } = useProducts();
  const {
    products,
    meta,
    isLoading,
    isLoadingMore,
    isEmpty,
    hasMore,
    loadMore,
  } = useInfiniteProductList(filters, 12);

  const { useAllCategories } = useCategories();
  const { categories, isLoading: isCategoriesLoading } = useAllCategories();

  // Root kategorileri filtrele (Sidebar icin)
  const rootCategories = categories.filter((c: Category) => !c.parentId);

  const handleCategoryChange = (slug: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (slug) {
      newParams.set('categorySlug', slug);
      newParams.delete('categoryId');
    } else {
      newParams.delete('categorySlug');
      newParams.delete('categoryId');
    }
    newParams.delete('page');
    setSearchParams(newParams);
    setIsMobileFiltersOpen(false);
  };

  // IntersectionObserver: sayfa sonuna yaklaşınca loadMore çağır
  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [hasMore, isLoadingMore, loadMore]);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: '400px', // 400px erken tetikle - kullanıcı hissetmeden yükle
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleObserver]);

  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Header Banner */}
      <div className="bg-gray-50 border-b border-gray-100 py-12 md:py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-5xl font-display font-light text-gray-900 mb-4 tracking-tight">
            Koleksiyonumuz
          </h1>
          <p className="text-gray-500 max-w-2xl mx-auto font-light text-lg">
            Estetik ve fonksiyonelliğin mükemmel uyumu. Her bir parçası özenle tasarlanmış mobilya koleksiyonumuzu keşfedin.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-12">

          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-28 space-y-8">

              {/* Categories Filter */}
              <div>
                <h3 className="font-display font-medium text-lg mb-4 border-b pb-2">Kategoriler</h3>
                {isCategoriesLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3, 4].map(i => <div key={i} className="h-6 bg-gray-100 rounded animate-pulse" />)}
                  </div>
                ) : (
                  <ul className="space-y-2">
                    <li>
                      <button
                        onClick={() => handleCategoryChange(null)}
                        className={cn(
                          "text-sm w-full text-left py-1 transition-colors hover:text-black",
                          !filters.categorySlug ? "font-bold text-black" : "text-gray-500 font-medium"
                        )}
                      >
                        Tüm Ürünler
                      </button>
                    </li>
                    {rootCategories.map((cat) => (
                      <li key={cat.id}>
                        <button
                          onClick={() => handleCategoryChange(cat.slug)}
                          className={cn(
                            "text-sm w-full text-left py-1 transition-colors hover:text-black",
                            filters.categorySlug === cat.slug ? "font-bold text-black" : "text-gray-500 font-medium"
                          )}
                        >
                          {cat.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Featured Filter */}
              <div>
                <h3 className="font-display font-medium text-lg mb-4 border-b pb-2">Filtrele</h3>
                <label className="flex items-center space-x-3 cursor-pointer group">
                  <div className={cn(
                    "w-5 h-5 border rounded flex items-center justify-center transition-colors",
                    filters.featured ? "bg-black border-black" : "border-gray-300 group-hover:border-gray-400"
                  )}>
                    {filters.featured && <div className="w-2.5 h-2.5 bg-white" />}
                  </div>
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={!!filters.featured}
                    onChange={(e) => {
                      const newParams = new URLSearchParams(searchParams);
                      if (e.target.checked) newParams.set('featured', 'true');
                      else newParams.delete('featured');
                      newParams.delete('page');
                      setSearchParams(newParams);
                    }}
                  />
                  <span className="text-sm text-gray-600 font-medium group-hover:text-black">Öne Çıkanlar</span>
                </label>
              </div>

            </div>
          </aside>

          {/* Mobile Filter Button */}
          <div className="lg:hidden flex justify-end items-center bg-gray-50 p-4">
            <button
              onClick={() => setIsMobileFiltersOpen(true)}
              className="flex items-center space-x-2 text-sm font-bold uppercase tracking-wide border border-gray-300 px-4 py-2 rounded bg-white"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              <span>Filtrele</span>
            </button>
          </div>

          {/* Main Content */}
          <main className="flex-1">
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="aspect-[3/4] bg-gray-100 animate-pulse" />
                ))}
              </div>
            ) : isEmpty ? (
              <div className="text-center py-32 bg-gray-50 border border-gray-100">
                <div className="w-16 h-16 bg-white flex items-center justify-center mx-auto mb-6 shadow-sm">
                  <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <h3 className="font-display text-xl text-gray-900 mb-2">Ürün Bulunamadı</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  Aradığınız kriterlere uygun ürün mevcut değil veya sunucuya erişilemiyor.
                </p>
                <button
                  onClick={() => handleCategoryChange(null)}
                  className="mt-8 px-8 py-3 bg-black text-white text-sm font-medium hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl"
                >
                  Tüm Koleksiyonu Gör
                </button>
              </div>
            ) : (
              <>
                {/* Product count */}
                {meta && (
                  <p className="text-sm text-gray-400 mb-6">
                    {meta.total} ürün
                  </p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {products.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                    />
                  ))}
                </div>

                {/* Infinite scroll trigger */}
                <div ref={loadMoreRef} className="w-full py-8 flex justify-center">
                  {isLoadingMore && hasMore && (
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                      <span className="text-sm">Yükleniyor...</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </main>
        </div>
      </div>

      {/* Mobile Filters Drawer */}
      <AnimatePresence>
        {isMobileFiltersOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileFiltersOpen(false)}
              className="fixed inset-0 bg-black z-50 lg:hidden"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween' }}
              className="fixed right-0 top-0 bottom-0 w-80 bg-white z-50 p-6 overflow-y-auto lg:hidden shadow-2xl"
            >
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-xl font-bold font-display">Filtreler</h2>
                <button onClick={() => setIsMobileFiltersOpen(false)}>
                  <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-8">
                <div>
                  <h3 className="font-bold text-sm uppercase tracking-widest mb-4">Kategoriler</h3>
                  <ul className="space-y-3">
                    <li>
                      <button onClick={() => handleCategoryChange(null)} className={cn("block w-full text-left", !filters.categorySlug ? "font-bold text-black" : "text-gray-600")}>
                        Tüm Ürünler
                      </button>
                    </li>
                    {rootCategories.map((cat) => (
                      <li key={cat.id}>
                        <button
                          onClick={() => handleCategoryChange(cat.slug)}
                          className={cn("block w-full text-left", filters.categorySlug === cat.slug ? "font-bold text-black" : "text-gray-600")}
                        >
                          {cat.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
